import posthog from 'posthog-js';

// Only initialize PostHog on the client side
if (typeof window !== 'undefined') {
  const isEnabled = process.env.NEXT_PUBLIC_POSTHOG_ENABLED === 'true';
  const isDev = process.env.NODE_ENV === 'development';
  const posthogToken = process.env.NEXT_PUBLIC_POSTHOG_KEY || process.env.NEXT_PUBLIC_POSTHOG_TOKEN;
  const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

  // Initialize PostHog if enabled (especially in dev environments where we may want to disable it)
  if ((isEnabled || !isDev) && posthogToken) {
    try {
      posthog.init(posthogToken, {
        api_host: '/ingest',
        ui_host: posthogHost,
        // Only identify events when explicitly called - this is more cost effective
        person_profiles: 'identified_only',
        // Disable in development unless explicitly enabled
        loaded: (posthog) => {
          if (isDev && !isEnabled) {
            // console.log('PostHog tracking disabled in development. Set NEXT_PUBLIC_POSTHOG_ENABLED=true to enable.');
            posthog.opt_out_capturing();
          } else {
            // console.log('PostHog initialized successfully');
          }
        },
        // Add security and stability settings
        persistence: 'localStorage+cookie',
        cross_subdomain_cookie: true,
        secure_cookie: true,
        // Keep session recording enabled but optimize other aspects
        disable_scroll_properties: true, // Don't track every scroll event
      });
    } catch (error) {
      // console.warn('Failed to initialize PostHog:', error);
    }
  } else {
    // console.log('PostHog tracking disabled or token missing.');
    // Make sure posthog exists even if not initialized
    if (!posthog.__loaded) {
      posthog.__loaded = true;
      posthog.opt_out_capturing();
    }
  }
}

// Helper function to identify users
export const identifyUser = (userId: string, traits?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog.__loaded) {
    try {
      posthog.identify(userId, traits);
    } catch (error) {
      // console.error('Error identifying user with PostHog:', error);
    }
  }
};

// Helper function to capture events
export const captureEvent = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog.__loaded) {
    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      // console.error('Error capturing event with PostHog:', error);
    }
  }
};

// A/B Testing Utilities
export const getABTestGroup = (userId: string, testName: string): 'A' | 'B' => {
  if (!userId) return 'A'; // Default to control group for anonymous users

  // Create a consistent hash based on userId and testName
  const input = `${userId}-${testName}`;
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Return A or B based on hash (50/50 split)
  return Math.abs(hash) % 2 === 0 ? 'A' : 'B';
};

// Track A/B test exposure
export const trackABTestExposure = (testName: string, variant: string, userId?: string) => {
  captureEvent('ab_test_exposure', {
    test_name: testName,
    variant: variant,
    user_id: userId,
    timestamp: new Date().toISOString()
  });
};

// Track A/B test conversion events
export const trackABTestConversion = (testName: string, variant: string, conversionEvent: string, properties?: Record<string, any>) => {
  captureEvent('ab_test_conversion', {
    test_name: testName,
    variant: variant,
    conversion_event: conversionEvent,
    timestamp: new Date().toISOString(),
    ...properties
  });
};

// Default export for direct usage
export default posthog; 